import {parseDate} from 'common/date';
import {Unlink} from 'lucide-react';
import * as React from 'react';
import {Link} from 'react-router-dom';
import {Button} from 'src/Button';
import {formatCentsSigned} from 'src/CardBenefitsView/benefitUtils';
import {useModals} from 'src/Modals';
import type {BenefitRawTransactionFragment} from '@transactions/graphql';

type Props = {
  rawTransactions: readonly BenefitRawTransactionFragment[];
  /** Shown in the unlink confirmation when the caller knows the benefit */
  benefitName?: string;
  emptyText?: string;
  onUpdate: () => void;
};

/**
 * Compact list of the raw transactions linked to a benefit, with navigation
 * to each raw transaction and an unlink action (behind a confirmation).
 */
export const BenefitTransactionsList = (props: Props) => {
  const {rawTransactions, benefitName, emptyText, onUpdate} = props;
  const {unlinkBenefitTransactionModal} = useModals();

  const handleUnlink = (rawTransaction: BenefitRawTransactionFragment) => {
    unlinkBenefitTransactionModal.openModal({
      rawTransaction,
      benefitName,
      onUnlinked: onUpdate,
    });
  };

  if (rawTransactions.length === 0) {
    return (
      <p className="text-muted-foreground text-xs italic">
        {emptyText ?? 'No linked transactions in this period.'}
      </p>
    );
  }

  return (
    <div className="flex flex-col">
      {rawTransactions.map((rt) => (
        <div
          key={rt._id}
          className="group flex items-center gap-2 rounded py-1"
        >
          <Link
            to={`/raw-transactions/${rt._id}`}
            className="hover:bg-accent flex min-w-0 flex-1 items-baseline gap-2 rounded px-1 py-0.5"
          >
            <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
              {parseDate(rt.date).toLocaleString({
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
            <span className="min-w-0 flex-1 truncate text-xs">
              {rt.description}
            </span>
            <span className="shrink-0 text-xs tabular-nums">
              {formatCentsSigned(rt.amount)}
            </span>
          </Link>
          <Button
            size="icon"
            variant="ghost"
            className="size-7 shrink-0"
            title="Unlink from benefit"
            onClick={() => handleUnlink(rt)}
          >
            <Unlink className="size-4" />
          </Button>
        </div>
      ))}
    </div>
  );
};
